/**
 * Plays the Vimeo video within a given media carousel item.
 * Hides the image, overlay, and play button.
 * Implements lazy loading by only loading the video when needed.
 *
 * @param {HTMLElement} container The media carousel item element.
 */
// Make playVideo function globally accessible
window.playVideo = function (container) {
  if (!container) return;

  const playButton = container.querySelector('.media-carousel-play');
  const videoSource = playButton?.dataset.source || container.dataset.source;
  const img = container.querySelector('img');
  const overlay = container.querySelector('.bg-opacity-60');
  const iframe = container.querySelector('.media-carousel-iframe');
  const shouldLoop = container.dataset.loop === 'true';

  if (!videoSource) return; // No video source found

  // Hide elements
  if (playButton) playButton.style.display = 'none';
  if (img) img.style.display = 'none';
  if (overlay) overlay.style.display = 'none';

  // Check if this is a Vimeo URL
  if (videoSource.includes('vimeo.com') && iframe) {
    let embedUrl = '';
    let videoId = null;

    // Get the source URL from data-src attribute
    const iframeSrc = iframe.dataset.src || videoSource;

    // Extract Vimeo video ID
    const videoIdMatch = iframeSrc.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (videoIdMatch && videoIdMatch[1]) {
      videoId = videoIdMatch[1];

      // Create Vimeo embed URL with custom parameters:
      // - autoplay=1: Start playing automatically
      // - background=1: Hide controls, logo, etc.
      // - muted=0: Enable sound
      // - controls=0: Hide controls
      embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&background=1&muted=0&controls=0`;

      // Add loop parameter if needed
      if (shouldLoop) {
        embedUrl += '&loop=1';
      }
    }

    // Store the video ID and playing state in the container for later use
    container.dataset.videoId = videoId;
    container.dataset.playing = 'true';

    // Add a class to mark this iframe as manually played
    container.classList.add('video-manually-played');

    // Only set the src when we're ready to play
    iframe.src = embedUrl;
    iframe.style.display = 'block';

    // Add click event to toggle play/pause
    if (!iframe.hasAttribute('data-click-listener')) {
      iframe.setAttribute('data-click-listener', 'true');

      // Use a transparent overlay for click events since iframe might not capture clicks
      const clickOverlay = document.createElement('div');
      clickOverlay.className = 'absolute inset-0 z-40 cursor-pointer video-click-overlay';
      container.appendChild(clickOverlay);

      clickOverlay.addEventListener('click', () => {
        // Toggle play/pause using Vimeo's postMessage API
        const isPlaying = container.dataset.playing === 'true';

        if (isPlaying) {
          // Pause the video
          iframe.contentWindow.postMessage(JSON.stringify({
            method: 'pause'
          }), '*');
          container.dataset.playing = 'false';
        } else {
          // Play the video
          iframe.contentWindow.postMessage(JSON.stringify({
            method: 'play'
          }), '*');
          container.dataset.playing = 'true';
        }
      });
    }
  }
};

// --- Manual Play Button Click Handler ---
document.querySelectorAll('.media-carousel-play').forEach(button => {
  button.addEventListener('click', (e) => {
    const container = e.currentTarget.closest('.media-carousel-item');
    if (container) {
      playVideo(container);
    }
  });
});

// --- Handle hover events for play/pause ---
// For videos that are manually started with the play button, we don't want to
// pause them when the mouse leaves. We'll completely remove the mouseleave handler.
document.querySelectorAll('.media-carousel-item').forEach(item => {
  item.addEventListener('mouseenter', () => {
    const videoPlayer = item.querySelector('.media-carousel-video');
    const isActiveSlide = item.closest('.swiper-slide').classList.contains('swiper-slide-active');

    // Only play on hover if it's the active slide and the video is displayed
    if (videoPlayer && videoPlayer.style.display === 'block' && isActiveSlide) {
      videoPlayer.play().catch(error => console.error("Video play failed on hover:", error));
    }
  });

  // We're completely removing the mouseleave handler to prevent videos from pausing
  // when the mouse leaves the video area. This ensures videos continue playing.
  // Videos will only stop when the slide changes or when the user manually pauses them.
});

// --- Intersection Observer for initial page load autoplay ---
// This is a fallback for when the carousel is visible on page load
const observerOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0.7 // Trigger when 70% of the element is visible
};

const observerCallback = (entries, observer) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const container = entry.target;
      const shouldAutoplay = container.dataset.autoplay === 'true';
      const showPlayBtn = container.dataset.showPlayBtn === 'true';
      const isActiveSlide = container.closest('.swiper-slide').classList.contains('swiper-slide-active');

      // Only autoplay if:
      // 1. The autoplay flag is true
      // 2. There is no play button (or play button is not shown)
      // 3. The slide is the active slide in the carousel
      if (shouldAutoplay && !showPlayBtn && isActiveSlide) {
        // Play after a short delay
        setTimeout(() => {
          playVideo(container);
        }, 500);

        // Stop observing after playing
        observer.unobserve(container);
      }
    }
  });
};

// Create observer and observe autoplay items
const observer = new IntersectionObserver(observerCallback, observerOptions);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Set up manual play button click handlers
  document.querySelectorAll('.media-carousel-play').forEach(button => {
    if (!button.hasAttribute('data-click-listener-added')) {
      button.setAttribute('data-click-listener-added', 'true');
      button.addEventListener('click', (e) => {
        const container = e.currentTarget.closest('.media-carousel-item');
        if (container) {
          window.playVideo(container);
        }
      });
    }
  });

  // Observe autoplay items
  document.querySelectorAll('.media-carousel-item[data-autoplay="true"]').forEach(item => {
    observer.observe(item);
  });
});
